import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PencilSimpleIcon } from '@phosphor-icons/react/dist/csr/PencilSimple';
import { PlusIcon } from '@phosphor-icons/react/dist/csr/Plus';
import { TrashIcon } from '@phosphor-icons/react/dist/csr/Trash';

import { RoleFormDialog } from './RoleFormDialog';
import styles from './RoleScreen.module.css';
import { PageActions } from '../../app/shell/PageActions';
import { Button } from '../../design-system/primitives/Button/Button';
import { ClientDataTable } from '../../design-system/composites/DataTable/ClientDataTable';
import type { Column, Row } from '../../design-system/composites/DataTable/DataTable';
import { ApiError } from '../../lib/api/client';
import {
  permissionsApi,
  rolesApi,
  type Permission,
  type Role,
} from '../../lib/api/users';

/**
 * Roles, and what each one may do.
 *
 * ## What this screen may NOT do, and why the server is what says so
 *
 * `administrator` cannot be touched at all; a built-in cannot be renamed or
 * deleted; seven permissions are administrator-only; a role with holders
 * cannot be deleted. Every one of those is enforced by the API, and this
 * screen READS the answer rather than re-deriving it — `built_in`,
 * `protected` and `reserved` all arrive on the payload.
 *
 * That is deliberate. A screen carrying its own copy of "administrator is
 * special" is a second statement of the rule, and the copy that drifts is
 * always the one on the screen. Here the controls simply reflect what the
 * server already published, and a refusal that slips through still arrives as
 * a plain error rather than a broken action.
 *
 * ## Reserved permissions are shown, not hidden
 *
 * Disabled, with the reason beside them. An administrator who cannot find
 * `user.create` in the list concludes the feature is broken; one who sees it
 * greyed out with "administrator only" beside it has their answer.
 */

type ListStatus = 'loading' | 'ready' | 'empty' | 'error';

interface RoleRow extends Row, Role {
  /** Flattened for the table: `ClientDataTable` sorts and searches primitives. */
  label: string;
  permission_count: number;
}

export interface RoleScreenProps {
  permissions: readonly string[];
}

export function RoleScreen({ permissions }: RoleScreenProps) {
  const { t, i18n } = useTranslation();

  const [status, setStatus] = useState<ListStatus>('loading');
  const [rows, setRows] = useState<readonly RoleRow[]>([]);
  const [catalogue, setCatalogue] = useState<readonly Permission[]>([]);
  const [reference, setReference] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Role | undefined>(undefined);
  const [failure, setFailure] = useState<string | null>(null);

  const canManage = permissions.includes('role.manage');

  const toRow = useCallback(
    (role: Role): RoleRow => ({
      ...role,
      id: role.uuid,
      label: i18n.language.startsWith('ar') ? role.label_ar : role.label_en,
      permission_count: role.permissions?.length ?? 0,
    }),
    [i18n.language],
  );

  const load = useCallback(async () => {
    setStatus('loading');
    setReference(null);

    try {
      const [roles, catalogueRows] = await Promise.all([rolesApi.list(), permissionsApi.list()]);
      setCatalogue(catalogueRows);
      const mapped = roles.map(toRow);
      setRows(mapped);
      setStatus(mapped.length === 0 ? 'empty' : 'ready');
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      setReference(error.requestId ?? null);
      setStatus('error');
    }
  }, [toRow]);

  useEffect(() => {
    void load();
  }, [load]);

  async function remove(role: Role): Promise<void> {
    setFailure(null);

    try {
      await rolesApi.remove(role.uuid);
      setRows((current) => current.filter((row) => row.id !== role.uuid));
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      /*
       * The SERVER'S message, not one of ours. A 409 here says how many
       * accounts still hold the role, and a generic "could not delete" would
       * throw away the only part the administrator can act on.
       */
      setFailure(error.message);
    }
  }

  function renderPermissions(row: Row): string {
    const role = row as RoleRow;

    return role.permissions === undefined
      ? '—'
      : String(role.permission_count);
  }

  function renderKind(row: Row): string {
    const role = row as RoleRow;

    if (role.protected) return t('roles.kindProtected');

    return role.built_in ? t('roles.kindBuiltIn') : t('roles.kindCustom');
  }

  function renderActions(row: Row) {
    const role = row as RoleRow;

    if (!canManage || role.protected) return null;

    return (
      <div className={styles['actions']}>
        <Button
          variant="ghost"
          icon={<PencilSimpleIcon size={16} />}
          onClick={() => {
            setEditing(role);
            setFormOpen(true);
          }}
        >
          {t('roles.edit')}
        </Button>

        {/* A built-in cannot be deleted at any holder count — the server
            refuses it, and offering the control would be an invitation to
            find that out the hard way. */}
        {role.built_in ? null : (
          <Button
            variant="ghost"
            icon={<TrashIcon size={16} />}
            onClick={() => {
              void remove(role);
            }}
          >
            {t('roles.delete')}
          </Button>
        )}
      </div>
    );
  }

  const columns: readonly Column[] = [
    { key: 'label', label: t('roles.columnLabel') },
    { key: 'name', label: t('roles.columnName'), neverMirrors: true },
    { key: 'kind', label: t('roles.columnKind'), render: renderKind },
    { key: 'permission_count', label: t('roles.columnPermissions'), render: renderPermissions },
    { key: 'holders', label: t('roles.columnHolders') },
    { key: 'actions', label: t('roles.columnActions'), render: renderActions },
  ];

  return (
    <div className={styles['page']}>
      {canManage ? (
        <PageActions>
          <Button
            icon={<PlusIcon size={16} />}
            onClick={() => {
              setEditing(undefined);
              setFormOpen(true);
            }}
          >
            {t('roles.add')}
          </Button>
        </PageActions>
      ) : null}

      <ClientDataTable
        /* The intro sits in the table card's toolbar, at data size, rather
           than as muted text on the page ground. */
        toolbarActions={<p className={styles['intro']}>{t('roles.intro')}</p>}
        caption={t('roles.title')}
        columns={columns}
        rows={rows}
        status={status}
        empty={{ body: t('roles.empty') }}
        error={{ title: t('roles.loadFailed') }}
        onRetry={() => {
          void load();
        }}
      />

      <div className={styles['failure']} role="alert" aria-atomic="true">
        {failure}
      </div>

      {reference === null ? null : (
        <p className={styles['reference']}>
          {t('auth.supportReference')}{' '}
          <bdi dir="ltr">{reference}</bdi>
        </p>
      )}

      <RoleFormDialog
        open={formOpen}
        role={editing}
        catalogue={catalogue}
        onClose={() => {
          setFormOpen(false);
        }}
        onSaved={(saved) => {
          setRows((current) => {
            const exists = current.some((row) => row.id === saved.uuid);

            return exists
              ? current.map((row) => (row.id === saved.uuid ? toRow(saved) : row))
              : [...current, toRow(saved)];
          });
          setStatus('ready');
          setFormOpen(false);
        }}
      />
    </div>
  );
}
